import React from "react";
import { CheckCircle2, Clock, FileUp, CreditCard, UserCheck, FileSearch, FileCheck2, Star } from "lucide-react";

/**
 * 6-stage case tracker — derived purely from the consultation document
 * fields already returned by /api/consultations. No new backend call.
 *
 * Stage flow:
 *  1. Booked        — case created
 *  2. Paid          — payment_status == "paid"
 *  3. Assigned      — doctor_id present  (always true at booking — kept for clarity)
 *  4. Under Review  — status == "under_review"
 *  5. Report Ready  — status in ("report_ready", "completed")
 *  6. Reviewed      — has_review === true
 */
function stagesFor(c) {
  const paid = c.payment_status === "paid";
  const inReview = ["under_review", "report_ready", "completed"].includes(c.status);
  const reportReady = ["report_ready", "completed"].includes(c.status);
  const reviewed = !!c.has_review;
  return [
    { id: "booked",   label: "Booked",        icon: FileUp,     done: true,        ts: c.created_at },
    { id: "paid",     label: "Paid",          icon: CreditCard, done: paid,        ts: paid ? c.paid_at : null },
    { id: "assigned", label: "Doctor assigned", icon: UserCheck, done: paid,       ts: paid ? c.assigned_at || c.paid_at : null },
    { id: "review",   label: "Under review",  icon: FileSearch, done: inReview,    ts: c.review_started_at },
    { id: "ready",    label: "Report ready",  icon: FileCheck2, done: reportReady, ts: c.report_ready_at || c.completed_at },
    { id: "rated",    label: "You reviewed",  icon: Star,       done: reviewed,    ts: c.reviewed_at },
  ];
}

/** ETA helper — humanise the "what's next" copy. */
function nextAction(c, stages) {
  if (!stages[1].done) return { text: "Pay to start specialist review", tone: "amber" };
  if (!stages[3].done) return { text: "Specialist will start review within 24 hours", tone: "blue" };
  if (!stages[4].done) return { text: "Report typically delivered within 48 hours of review start", tone: "blue" };
  if (!stages[5].done) return { text: "Tap to read your report and share a quick review", tone: "emerald" };
  return { text: "Case completed — thank you!", tone: "emerald" };
}

const toneClass = {
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  blue:    "bg-blue-50 text-blue-800 border-blue-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function CaseTracker({ case: c }) {
  const stages = stagesFor(c);
  const next = nextAction(c, stages);
  const doneCount = stages.filter(s => s.done).length;
  const pct = Math.round((doneCount / stages.length) * 100);

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`case-tracker-${c.id}`}>
      {/* Progress bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Case progress</div>
        <div className="text-[11px] font-semibold text-slate-700">{doneCount}/{stages.length}</div>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-700"
          style={{ width: `${pct}%` }}
          data-testid={`case-tracker-bar-${c.id}`}
        />
      </div>

      {/* 6 stage steps — horizontal, scrollable on mobile */}
      <div className="mt-4 grid grid-cols-6 gap-1.5">
        {stages.map((s, i) => {
          const Icon = s.icon;
          const active = s.done && !stages[i + 1]?.done;
          return (
            <div
              key={s.id}
              className="flex flex-col items-center text-center min-w-0"
              data-testid={`case-stage-${c.id}-${s.id}`}
              title={s.ts ? `${s.label} · ${new Date(s.ts).toLocaleString()}` : s.label}
            >
              <div
                className={`relative w-8 h-8 sm:w-9 sm:h-9 rounded-full grid place-items-center transition ${
                  s.done
                    ? active
                      ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white ring-4 ring-blue-100 shadow"
                      : "bg-emerald-500 text-white"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {s.done && !active ? <CheckCircle2 className="w-4 h-4"/> : <Icon className="w-4 h-4"/>}
                {active && (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-blue-400 animate-ping"/>
                )}
              </div>
              <div className={`mt-1.5 text-[10px] sm:text-[11px] leading-tight ${s.done ? "text-slate-800 font-semibold" : "text-slate-400"}`}>
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* What's next chip */}
      <div className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${toneClass[next.tone]}`} data-testid={`case-next-${c.id}`}>
        <Clock className="w-3 h-3"/> {next.text}
      </div>
    </div>
  );
}
